import { useEffect, useState } from "react";
import api from "../services/api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  const [summary, setSummary] = useState({});
  const [recent, setRecent] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [sales7, setSales7] = useState([]);
  const [branchSales, setBranchSales] = useState([]);

  useEffect(() => {
    document.title = "Dashboard | Retail POS";
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const requests = [
        api.get("/dashboard/summary"),
        api.get("/dashboard/recent-invoices"),
        api.get("/dashboard/low-stock"),
       api.get("/dashboard/sales-7days")
      ];

      // 🔒 Only admin sees branch-wise sales
      if (["ADMIN", "SUPER_ADMIN"].includes(user.role)) {
        requests.push(api.get("/dashboard/branch-sales"));
      }

      const [
        summaryRes,
        recentRes,
        lowStockRes,
        sales7Res,
        branchSalesRes
      ] = await Promise.all(requests);

      setSummary(summaryRes.data || {});
      setRecent(recentRes.data || []);
      setLowStock(lowStockRes.data || []);

      setSales7(
        Array.isArray(sales7Res.data)
          ? sales7Res.data.map(d => ({
              date: d.date,
              total: Number(d.total) || 0
            }))
          : []
      );

      if (branchSalesRes) {
        setBranchSales(
          Array.isArray(branchSalesRes.data)
            ? branchSalesRes.data.map(b => ({
                branch: b.branch_name,
                total: Number(b.total) || 0
              }))
            : []
        );
      }
    } catch (err) {
      console.error("Dashboard load error", err);
    }
  };

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>

      {/* KPI CARDS */}
      <div className="kpi-grid">
        <Card title="Today Sales" value={`₹${summary.todaySales}`} />
        <Card title="Today Invoices" value={summary.todayInvoices} />
        <Card title="Month Sales" value={`₹${summary.monthSales}`} />
        <Card title="Products" value={summary.totalProducts} />
        <Card title="Low Stock" value={summary.lowStock} danger />
      </div>

      {/* CHARTS */}
      <div className="chart-grid">
        {/* LAST 7 DAYS */}
        <div className="chart-card">
          <h3>Last 7 Days Sales</h3>

          {sales7.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={sales7}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={d => d.slice(5)} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={v => `₹${v}`} />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#2563eb"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty">No sales data</div>
          )}
        </div>

        {/* BRANCH SALES – ADMIN ONLY */}
        {["ADMIN", "SUPER_ADMIN"].includes(user.role) && (
          <div className="chart-card">
            <h3>Branch-wise Sales</h3>

            {branchSales.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={branchSales}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="branch" />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={v => `₹${v}`} />
                  <Bar
                    dataKey="total"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty">No branch sales data</div>
            )}
          </div>
        )}
      </div>

      {/* RECENT INVOICES */}
      <section>
        <h3>Recent Invoices</h3>
        <div className="invoice-table print-container">
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Branch</th>
                <th>Total</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(i => (
                <tr key={i.invoice_id}>
                  <td>{i.invoice_number}</td>
                  <td>{i.branch?.branch_name}</td>
                  <td>₹{i.grand_total}</td>
                  <td>{new Date(i.invoice_date).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

const Card = ({ title, value, danger }) => (
  <div className={`kpi-card ${danger ? "danger" : ""}`}>
    <div className="kpi-title">{title}</div>
    <div className="kpi-value">{value ?? "-"}</div>
  </div>
);

export default Dashboard;
